import React, { useEffect, useRef, useState } from 'react';
import 'altcha';
import LoadingDots from './LoadingDots';

interface AltchaElement extends HTMLElement {
  challengeurl: string;
  auto: string;
  hidelogo: boolean;
  hidefooter: boolean;
  debug: boolean;
}

interface AltchaWidgetProps {
  challengeUrl: string;
  onVerified: (payload: string) => void;
  onError?: (error: string) => void;
  auto?: boolean;
}

const AltchaWidget: React.FC<AltchaWidgetProps> = ({
  challengeUrl,
  onVerified,
  onError,
  auto = true
}) => {
  const widgetRef = useRef<AltchaElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if we're in a test environment
  const isTestEnvironment =
    typeof window !== 'undefined' &&
    (window as unknown as { cypressMockAltcha?: boolean }).cypressMockAltcha === true;

  // If in test environment, immediately call onVerified and return null
  useEffect(() => {
    if (isTestEnvironment) {
      setTimeout(() => {
        onVerified('cypress-mock-altcha-payload');
      }, 100);
    }
  }, [isTestEnvironment, onVerified]);

  useEffect(() => {
    if (isTestEnvironment) return; // Skip all ALTCHA logic in tests

    const widget = widgetRef.current;
    if (!widget) return;

    if (auto) {
      timeoutRef.current = setTimeout(() => {
        if (!isLoaded) {
          onError?.('Sicherheitsprüfung dauert länger als erwartet. Einen Moment bitte...');

          setTimeout(() => {
            if (widget) {
              widget.challengeurl = widget.challengeurl + '?retry=' + Date.now();
            }
          }, 2000);
        }
      }, 60000);
    }

    const handleStateChange = (event: CustomEvent) => {
      const { state, payload } = event.detail;

      if (state === 'verified' && payload) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        onVerified(payload);
      } else if (state === 'error') {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        onError?.(`ALTCHA Error: ${event.detail.error || 'Unbekannter Fehler'}`);
      }
    };

    const handleLoad = () => {
      setIsLoaded(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    widget.addEventListener('statechange', handleStateChange as EventListener);
    widget.addEventListener('load', handleLoad as EventListener);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      widget.removeEventListener('statechange', handleStateChange as EventListener);
      widget.removeEventListener('load', handleLoad as EventListener);
    };
  }, [onVerified, onError, auto, isLoaded, isTestEnvironment]);

  useEffect(() => {
    if (isTestEnvironment) return; // Skip all ALTCHA logic in tests

    const widget = widgetRef.current;
    if (widget) {
      widget.challengeurl = challengeUrl;
      if (auto) {
        widget.auto = 'onload';
      }
      widget.hidelogo = true;
      widget.hidefooter = true;
      widget.debug = false;
    }
  }, [challengeUrl, auto, isTestEnvironment]);

  // In test environment, render a mock success message
  if (isTestEnvironment) {
    return (
      <div
        style={{
          padding: '8px',
          background: '#f0f8f0',
          border: '1px solid #4caf50',
          borderRadius: '4px',
          margin: '8px 0',
          color: '#4caf50',
          fontSize: '14px',
          textAlign: 'center'
        }}
      >
        ✓ Sicherheitsprüfung erfolgreich (Test-Modus)
      </div>
    );
  }

  return (
    <div style={{ display: auto ? 'none' : 'block' }}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <altcha-widget
        ref={widgetRef}
        challengeurl={challengeUrl}
        auto={auto ? 'onload' : 'off'}
        hidelogo={true}
        hidefooter={true}
        debug={false}
        style={{
          display: auto ? 'none' : 'block',
          width: '100%',
          maxWidth: '400px'
        }}
      />
      {!isLoaded && auto && (
        <div
          style={{
            fontSize: '12px',
            color: '#666',
            textAlign: 'center',
            padding: '5px'
          }}
        >
          <LoadingDots />
        </div>
      )}
    </div>
  );
};

export default AltchaWidget;
